import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Mail, MessageSquare, Users, Phone, Headset, MessageCircle, Globe } from "lucide-react";
import { Icon } from "@iconify/react";
import SectionHeading from "@/components/SectionHeading";
import { useTranslation } from "react-i18next";
import { useTabIndicator } from "@/hooks/useTabIndicator";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from "@/components/ui/carousel";
import CTAButton from "@/components/CTAButton";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ncApi } from "@/services/api";
import { NETWORK_ERROR_MESSAGE } from "@/utils/axios";
import { intercomService } from "@/services/intercom";

const NewContactSection = () => {
  const { t } = useTranslation();
  const { activeTab, tabsListRef, indicatorStyle, handleTabChange } = useTabIndicator("contactus");
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMobileContactForm, setShowMobileContactForm] = useState(false);
  const [mobileActiveTab, setMobileActiveTab] = useState("contactus");

  // Synchronize mobile active tab with the desktop active tab
  useEffect(() => {
    setMobileActiveTab(activeTab);
  }, [activeTab]);

  // Update the desktop active tab when mobile active tab changes
  useEffect(() => {
    handleTabChange(mobileActiveTab);
  }, [mobileActiveTab]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    submitForm();
  };

  const submitForm = async () => {
    // 表单验证
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: t('contact.formError'),
        description: t('contact.formError'),
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // 调用联系我们API
      const response = await ncApi.contactUs({
        name: formData.name,
        email: formData.email,
        message: formData.message
      });

      // 请求成功
      toast({
        title: t('contact.success'),
        description: t('contact.successDescription'),
      });

      // 重置表单
      setFormData({ name: '', email: '', message: '' });
      setShowMobileContactForm(false);
    } catch (error) {
      // 请求失败
      toast({
        title: t('contact.error'),
        description: error.message || NETWORK_ERROR_MESSAGE,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to open Intercom messenger
  const openIntercomMessenger = async () => {
    try {
      await intercomService.openMessenger();
    } catch (error) {
      console.error('Failed to open Intercom messenger:', error);
      toast({
        title: t('contact.intercom.error', 'Failed to open chat'),
        description: t('contact.intercom.errorDesc', 'Please try again later or contact us via email.'),
        variant: "destructive"
      });
    }
  };

  const tabs = [
    {
      id: "contactus",
      icon: <Mail className="h-8 w-8 text-anzir" />,
      title: t('contact.tabs.contactus.title', "Contact Us"),
      description: t('contact.tabs.contactus.description', "Get in touch with our team"),
      link: "/contact",
      action: () => {
        // Show the mobile contact form instead of redirecting
        if (window.innerWidth < 768) {
          setMobileActiveTab("contactus");
          setShowMobileContactForm(true);
        } else {
          handleTabChange("contactus");
        }
      }
    },
    {
      id: "community",
      icon: <Users className="h-8 w-8 text-anzir" />,
      title: t('contact.tabs.community.title', "Community Help"),
      description: t('contact.tabs.community.description', "Get help from our community"),
      link: "/community",
      action: () => {
        if (window.innerWidth < 768) {
          setMobileActiveTab("community");
          setShowMobileContactForm(false);
        }
        handleTabChange("community");
      }
    }
  ];

  const communityCards = [
    {
      id: "discord",
      icon: <Icon icon="ic:baseline-discord" className="h-8 w-8 text-anzir" />,
      title: t('contact.community.discord.title', "Discord Community"),
      description: t('contact.community.discord.description', "Join our Discord community for real-time help and discussion"),
      link: "https://discord.com/invite/qr3HPC5AkH",
      action: () => window.open("https://discord.com/invite/qr3HPC5AkH", "_blank")
    },
    {
      id: "anzirCommunity",
      icon: <Globe className="h-8 w-8 text-anzir" />,
      title: t('contact.community.anzir.title', "Anzir Community"),
      description: t('contact.community.anzir.description', "Visit our community forum to find answers and share experiences"),
      link: "/community",
      action: () => window.location.href = "/community"
    }
  ];

  const contactInfo = [
    {
      icon: <Phone className="h-5 w-5" />,
      title: t('contact.info.call.title', 'Call Us'),
      details: '+61 414 408 150',
      link: 'tel:+61414408150'
    },
    {
      icon: <Mail className="h-5 w-5" />,
      title: t('contact.info.email.title', 'Email Us'),
      details: 'info@anzirtech.com',
      link: 'mailto:info@anzirtech.com'
    },
    {
      icon: <Headset className="h-5 w-5" />,
      title: t('contact.info.chat.title', 'Live Chat'),
      details: t('contact.info.chat.details', 'Chat with our team'),
      action: openIntercomMessenger
    }
  ];

  const handleCardClick = (tab) => {
    tab.action();
  };

  // Mobile contact form component for cleaner organization
  const MobileContactForm = () => (
    <motion.div
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <form onSubmit={handleSubmit} className="space-y-6 p-8 rounded-xl bg-black/60">
        <h3 className="text-xl font-semibold text-white mb-4">{t('contact.form')}</h3>
        <div className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="name" className="text-sm font-medium text-white">
              {t('contact.name')}
            </label>
            <Input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              required
              className="bg-black/40 border border-white/20 text-white"
              placeholder={t('contact.namePlaceholder')}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium text-white">
              {t('contact.emailAddress')}
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="bg-black/40 border border-white/20 text-white"
              placeholder={t('contact.emailPlaceholder')}
            />
          </div>
        </div>
        <div className="space-y-2">
          <label htmlFor="message" className="text-sm font-medium text-white">
            {t('contact.message')}
          </label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={5}
            className="bg-black/40 border border-white/20 text-white resize-none"
            placeholder={t('contact.messagePlaceholder')}
          />
        </div>
        <div className="flex gap-4">
          <CTAButton
            variant="default"
            className={`flex-1 bg-anzir hover:bg-anzir/90 ${isSubmitting ? 'opacity-70' : ''}`}
            onClick={submitForm}
          >
            {isSubmitting ? t('contact.sending') : t('contact.send')}
          </CTAButton>
          <CTAButton
            variant="outline"
            className="border-anzir text-anzir hover:bg-anzir/10"
            onClick={() => setShowMobileContactForm(false)}
          >
            {t('common.cancel', 'Cancel')}
          </CTAButton>
        </div>
      </form>
    </motion.div>
  );

  // Mobile community component for cleaner organization
  const MobileCommunityContent = () => (
    <motion.div
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      {communityCards.map((card) => (
        <motion.div
          key={card.id}
          className="flex flex-col items-center p-8 bg-black/60 rounded-xl hover:bg-black/80 transition-all transform hover:-translate-y-1 cursor-pointer"
          variants={itemVariants}
          onClick={() => handleCardClick(card)}
        >
          <div className="h-16 w-16 rounded-full bg-anzir/10 flex items-center justify-center mb-6">
            {card.icon}
          </div>
          <h3 className="text-xl font-semibold mb-3 text-white">{card.title}</h3>
          <p className="text-white/70 text-center mb-4">{card.description}</p>
          <CTAButton
            variant="outline"
            size="sm"
            className="mt-auto border-anzir text-anzir hover:bg-anzir hover:text-white"
            onClick={() => handleCardClick(card)}
          >
            {card.id === "discord" ? "Join Discord" : "Visit Forum"}
          </CTAButton>
        </motion.div>
      ))}
    </motion.div>
  );

  return (
    <section className="py-16 bg-black/80">
      <div className="container mx-auto px-4">
        <SectionHeading
          title={t('contact.title')}
          description={t('contact.description')}
          centered={true}
        />

        <div
          ref={tabsListRef}
          className="flex justify-center mt-10 relative mb-12"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              value={tab.id}
              className={`px-6 py-3 text-lg font-medium transition-colors ${
                activeTab === tab.id ? "text-white" : "text-white/50 hover:text-white/80"
              }`}
              onClick={() => {
                handleTabChange(tab.id);
                setMobileActiveTab(tab.id);
                if (tab.id === "contactus") {
                  setShowMobileContactForm(true);
                } else {
                  setShowMobileContactForm(false);
                }
              }}
            >
              {tab.title}
            </button>
          ))}
          <div
            className="absolute bottom-0 left-0 h-0.5 bg-anzir transition-all"
            style={indicatorStyle}
          />
        </div>

        <div className="md:hidden">
          {mobileActiveTab === "contactus" ? (
            showMobileContactForm ? (
              <MobileContactForm />
            ) : (
              <div className="flex flex-col items-center">
                <motion.div
                  className="flex flex-col items-center p-8 bg-black/60 rounded-xl hover:bg-black/80 transition-all mb-6 max-w-md w-full"
                  variants={itemVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  <div className="h-16 w-16 rounded-full bg-anzir/10 flex items-center justify-center mb-6">
                    <Mail className="h-8 w-8 text-anzir" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-white">{t('contact.tabs.contactus.title', "Contact Us")}</h3>
                  <p className="text-white/70 text-center mb-4">{t('contact.tabs.contactus.description', "Get in touch with our team")}</p>
                  <CTAButton
                    variant="outline"
                    className="mt-2 border-anzir text-anzir hover:bg-anzir hover:text-white"
                    onClick={() => setShowMobileContactForm(true)}
                  >
                    {t('contact.tabs.contactus.title', "Contact Us")}
                  </CTAButton>
                </motion.div>
              </div>
            )
          ) : (
            <MobileCommunityContent />
          )}
        </div>

        <div className="hidden md:block">
          {activeTab === "contactus" && (
            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <div className="md:col-span-2">
                <form onSubmit={handleSubmit} className="space-y-6 p-8 rounded-xl bg-black/60">
                  <h3 className="text-xl font-semibold text-white mb-4">{t('contact.form')}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="name" className="text-sm font-medium text-white">
                        {t('contact.name')}
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-black/40 border border-white/20 text-white"
                        placeholder={t('contact.namePlaceholder')}
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="email" className="text-sm font-medium text-white">
                        {t('contact.emailAddress')}
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-black/40 border border-white/20 text-white"
                        placeholder={t('contact.emailPlaceholder')}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="message" className="text-sm font-medium text-white">
                      {t('contact.message')}
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="bg-black/40 border border-white/20 text-white resize-none"
                      placeholder={t('contact.messagePlaceholder')}
                    />
                  </div>
                  <CTAButton
                    variant="default"
                    className={`w-full bg-anzir hover:bg-anzir/90 ${isSubmitting ? 'opacity-70' : ''}`}
                    onClick={submitForm}
                  >
                    {isSubmitting ? t('contact.sending') : t('contact.send')}
                  </CTAButton>
                </form>
              </div>
              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <div
                    key={index}
                    className={`flex items-start p-6 rounded-xl bg-black/60 ${item.action ? 'transition-all transform hover:-translate-y-1 cursor-pointer' : 'transition-all transform hover:-translate-y-1'}`}
                    onClick={item.action ? item.action : item.link ? () => window.open(item.link, "_blank") : undefined}
                  >
                    <div className="flex-shrink-0 mr-4">
                      <div className="p-3 rounded-full bg-anzir/10 text-anzir">
                        {item.icon}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-1 text-white">{item.title}</h3>
                      <p className="text-white/70">{item.details}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {activeTab === "community" && (
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {communityCards.map((card) => (
                <motion.div
                  key={card.id}
                  className="flex flex-col items-center p-8 bg-black/60 rounded-xl hover:bg-black/80 transition-all transform hover:-translate-y-1 cursor-pointer"
                  variants={itemVariants}
                  onClick={() => handleCardClick(card)}
                >
                  <div className="h-16 w-16 rounded-full bg-anzir/10 flex items-center justify-center mb-6">
                    {card.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-white">{card.title}</h3>
                  <p className="text-white/70 text-center mb-4">{card.description}</p>
                  <CTAButton
                    variant="outline"
                    size="sm"
                    className="mt-auto border-anzir text-anzir hover:bg-anzir hover:text-white"
                    onClick={() => handleCardClick(card)}
                  >
                    {card.id === "discord" ? "Join Discord" : "Visit Forum"}
                  </CTAButton>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default NewContactSection;
